import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  TextField, 
  IconButton, 
  Box,
  Typography,
  Divider,
  Alert,
  Paper,
  Button,
  DialogActions,
  Link
} from '@mui/material';
// Removed DownloadIcon usage after summary refactor
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCyclingToasts } from '../../hooks/useCyclingToasts';
import { RouteSummary } from '../common/RouteSummary';
import './SaveRouteModal.css';
// Formatters removed; use shared RouteSummary component

interface RouteMetrics {
  distance: number;
  duration: number;
  elevationGain: number;
}

interface SaveRouteModalProps {
  open: boolean;
  onClose: () => void;
  onDownload?: () => void; // optional GPX export
  onSave?: (routeName: string) => Promise<void> | void;
  currentBikeProfile?: string; // Current bike profile
  onValidateRouteName?: (routeName: string) => Promise<boolean>; // Returns true if name is valid (not duplicate)
  routeMetrics?: RouteMetrics; // Route metrics for display
  disabled?: boolean; // Whether the route is incomplete and buttons should be disabled
}

const SaveRouteModal: React.FC<SaveRouteModalProps> = ({
  open,
    onDownload,
  onClose,
  onSave,
  currentBikeProfile = 'trekking',
  onValidateRouteName,
  routeMetrics,
  disabled = false
}) => {
  const [routeName, setRouteName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const { user } = useAuth();
  const cyclingToasts = useCyclingToasts();
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Generate default route name with bike profile and date/time
  useEffect(() => {
    if (open) {
      // Always generate new default name with bike profile
      const now = new Date();
      const dateStr = now.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const timeStr = now.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      });
      setRouteName(`${currentBikeProfile} ${dateStr} ${timeStr}`);
      // Clear validation state when modal opens
      setNameError(null);
      // Reset focus state when modal opens
      setHasBeenFocused(false);
    }
  }, [open, currentBikeProfile]);

  // Validate route name when it changes (debounced)
  useEffect(() => {
    if (!routeName.trim() || !onValidateRouteName || !user) {
      setNameError(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsValidating(true);
      try {
        const isValid = await onValidateRouteName(routeName.trim());
        setNameError(isValid ? null : 'A route with this name already exists');
      } catch (error) {
        console.error('Failed to validate route name:', error);
        setNameError(null); // Don't show error for validation failures
      } finally {
        setIsValidating(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [routeName, onValidateRouteName, user]);

  const handleSave = async () => {
    if (onSave && routeName.trim() && !nameError && !isValidating) {
      try {
        // Close modal immediately so overlay can be seen
        onClose();
        await onSave(routeName.trim());
        cyclingToasts.routeSaved(routeName.trim());
      } catch (error) {
        // Show error toast
        const errorMessage = error instanceof Error ? error.message : 'Failed to save route';
        cyclingToasts.routeSaveError(errorMessage);
      }
    }
  };

    const handleDownload = () => {
      if (!onDownload) return;
      try {
        onDownload();
        cyclingToasts.routeExported('GPX');
      } catch (error) {
        cyclingToasts.showError('Failed to export route. Please try again.');
      }
    };
  // GPX export button removed from summary; still available in other contexts if needed.

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave();
    }
  };

  const handleFocus = () => {
    if (!hasBeenFocused && textFieldRef.current) {
      textFieldRef.current.select();
      setHasBeenFocused(true);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="save-route-modal"
      disableRestoreFocus={true}
    >
      <DialogTitle className="save-route-modal__title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon color="primary" fontSize="small" />
            Save Route
          </Typography>
          <IconButton 
            onClick={onClose}
            size="small"
            className="save-route-modal__close-button"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent className="save-route-modal__content">
        {/* Incomplete route warning */}
        {disabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Route is incomplete (cancelled or failed). Please retry route calculation before saving or downloading.
          </Alert>
        )}

        {/* Route Name Section */}
        <Box sx={{ mb: 2 }}>
          <Paper elevation={1} sx={{ p: 1.5, backgroundColor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <EditIcon color="primary" fontSize="small" />
              Route Name
            </Typography>
            <TextField
              inputRef={textFieldRef}
              fullWidth
              label="Enter route name"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={handleFocus}
              variant="outlined"
              className="save-route-modal__input"
              placeholder="Input your name of the route"
              disabled={!user}
              size="small"
              error={!!nameError}
              helperText={nameError}
            />
          </Paper>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Route Summary Section (shared) */}
        {routeMetrics && (
          <RouteSummary 
            distance={routeMetrics.distance}
            duration={routeMetrics.duration}
            elevationGain={routeMetrics.elevationGain}
            bikeProfile={currentBikeProfile}
          />
        )}

        <Box
          sx={{
            mt: 3,
            textAlign: 'center',
            backgroundColor: 'rgba(196, 227, 194, 0.4)',
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
            border: '1px solid rgba(14, 44, 38, 0.2)'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              color: 'var(--color-dark)',
              fontSize: '0.75rem'
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'info.main' }} />
            Quick heads-up: double-check your route and local rules before you ride.
          </Typography>
          <Link
            component={RouterLink}
            to="/ride-responsibly"
            onClick={() => {
              window.open('/ride-responsibly', '_blank');
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mt: 0.75,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'info.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Before You Ride – Important Info
          </Link>
        </Box>

        {/* Authentication warning - moved below disclaimer */}
        {!user && (
          <Alert 
            severity="warning" 
            sx={{ 
              mt: 2,
              fontSize: '0.75rem',
              '& .MuiAlert-message': {
                fontSize: '0.75rem'
              }
            }}
          >
            You need to be logged in to save routes. You can still download the route as GPX.
          </Alert>
        )}

      </DialogContent>

      {/* Dialog Actions - Cancel and Save buttons at bottom */}
      <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
        {onDownload && (
          <Button
            onClick={handleDownload}
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={disabled}
            title={disabled ? 'Cannot export incomplete route' : 'Download GPX'}
          >
            GPX
          </Button>
        )}
        <Button 
          onClick={onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={!user || !routeName.trim() || !!nameError || isValidating || disabled}
          startIcon={<SaveIcon />}
          color="primary"
          title={!user ? "Please log in to save routes" : disabled ? "Cannot save incomplete route" : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveRouteModal;
